import React from "react";
import { Upload, Image as ImageIcon, X } from "lucide-react";
import { api } from "../../lib/api";

function resolveUrl(u) {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    return `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

/**
 * Drag & drop image uploader.
 * onUploaded(url) is called with the API-relative URL after a successful upload.
 */
export default function ImageDropzone({ onUploaded, label = "Glissez une image ici ou cliquez pour choisir", testId = "image-dropzone" }) {
    const [dragging, setDragging] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [preview, setPreview] = React.useState(null);
    const [error, setError] = React.useState("");
    const inputRef = React.useRef(null);

    const handleFiles = async (files) => {
        const file = files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            setError("Le fichier doit être une image.");
            return;
        }
        if (file.size > 12 * 1024 * 1024) {
            setError("Image trop volumineuse (max 12 Mo).");
            return;
        }
        setError("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data.url);
            onUploaded?.(data.url);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Échec de l'upload");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const reset = () => {
        setPreview(null);
        onUploaded?.(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    return (
        <div data-testid={testId}>
            <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className={`relative cursor-pointer border border-dashed transition-colors p-6 text-center ${
                    dragging ? "border-[#c5a059] bg-[#c5a059]/5" : "border-white/20 hover:border-white/40"
                }`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid={`${testId}-input`}
                />
                {preview ? (
                    <div className="relative">
                        <img
                            src={resolveUrl(preview)}
                            alt="aperçu"
                            className="mx-auto max-h-44 object-contain"
                        />
                        <button
                            type="button"
                            data-testid={`${testId}-clear`}
                            onClick={(e) => { e.stopPropagation(); reset(); }}
                            className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:bg-red-500"
                            aria-label="Retirer"
                        >
                            <X size={14} strokeWidth={1.5} />
                        </button>
                    </div>
                ) : uploading ? (
                    <div className="py-8 text-zinc-400 text-sm">Upload en cours…</div>
                ) : (
                    <div className="py-8 flex flex-col items-center gap-3 text-zinc-400">
                        <Upload size={22} strokeWidth={1.5} className="text-[#c5a059]" />
                        <p className="text-sm">{label}</p>
                        <p className="text-xs text-zinc-600">JPG, PNG, WebP — max 12 Mo</p>
                    </div>
                )}
            </div>
            {error && <p data-testid={`${testId}-error`} className="text-red-400 text-xs mt-2">{error}</p>}
        </div>
    );
}
